import React, { useState } from "react";
import { useNotes } from "../../context/NotesContext";
import { LEAD_NAV } from "../../utils/navigation";
import Layout from "../../components/common/Layout";
import toast from "react-hot-toast";

const CATEGORIES = [
    { value: "general", label: "General" },
    { value: "reminder", label: "Reminder" },
    { value: "urgent", label: "Urgent" },
    { value: "todo", label: "To-Do" },
];

export default function LeadNotes() {
    const { notes, loading, addNote, updateNote, deleteNote, togglePinNote } = useNotes();
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState({ title: "", content: "", category: "general", reminderDate: "" });
    const [searchTerm, setSearchTerm] = useState("");
    const [filterCategory, setFilterCategory] = useState("all");

    // ── Handle form submit ──────────────────────────────────────
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.title.trim() || !formData.content.trim()) {
            toast.error("Please fill in all fields");
            return;
        }

        try {
            if (editingId) {
                await updateNote(editingId, formData.title, formData.content, formData.category, formData.reminderDate);
                toast.success("Note updated successfully");
                setEditingId(null);
            } else {
                await addNote(formData.title, formData.content, formData.category, formData.reminderDate);
                toast.success("Note created successfully");
            }
            setFormData({ title: "", content: "", category: "general", reminderDate: "" });
            setShowForm(false);
        } catch (err) {
            toast.error("Failed to save note: " + err.message);
        }
    };

    // ── Handle edit ────────────────────────────────────────────
    const handleEdit = (note) => {
        setFormData({ title: note.title, content: note.content, category: note.category, reminderDate: note.reminderDate || "" });
        setEditingId(note.id);
        setShowForm(true);
    };

    // ── Handle cancel ──────────────────────────────────────────
    const handleCancel = () => {
        setFormData({ title: "", content: "", category: "general", reminderDate: "" });
        setEditingId(null);
        setShowForm(false);
    };

    // ── Filter notes ───────────────────────────────────────────
    let filteredNotes = notes;
    if (filterCategory !== "all") {
        filteredNotes = filteredNotes.filter((n) => n.category === filterCategory);
    }
    if (searchTerm) {
        filteredNotes = filteredNotes.filter(
            (n) =>
                n.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                n.content.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }

    // Sort pinned notes first
    filteredNotes.sort((a, b) => (b.isPinned ? 1 : 0) - (a.isPinned ? 1 : 0));



    return (
        <Layout navItems={LEAD_NAV} panelLabel="My Notes">
            <div className="page-header">
                <div>
                    <h1 className="page-title">My Notes</h1>
                    <p className="page-subtitle">Keep track of your team reminders and notes</p>
                </div>
                <button
                    onClick={() => setShowForm(!showForm)}
                    className="btn btn-primary"
                >
                    {showForm ? "Cancel" : "+ Add Note"}
                </button>
            </div>

            {/* Form Section */}
            {showForm && (
                <div className="form-card" style={{ marginBottom: "1.5rem" }}>
                    <div className="card-header">
                        <h3 className="card-title">
                            {editingId ? "Edit Note" : "Create New Note"}
                        </h3>
                    </div>
                    <form onSubmit={handleSubmit} style={{ padding: "1.5rem" }}>
                        <div className="form-group">
                            <label>Title</label>
                            <input
                                type="text"
                                value={formData.title}
                                onChange={(e) =>
                                    setFormData({ ...formData, title: e.target.value })
                                }
                                placeholder="Enter note title"
                            />
                        </div>

                        <div className="form-group">
                            <label>Content</label>
                            <textarea
                                value={formData.content}
                                onChange={(e) =>
                                    setFormData({ ...formData, content: e.target.value })
                                }
                                placeholder="Enter note content"
                                rows="6"
                            />
                        </div>

                        <div className="form-group">
                            <label>Category</label>
                            <select
                                value={formData.category}
                                onChange={(e) =>
                                    setFormData({ ...formData, category: e.target.value })
                                }
                            >
                                {CATEGORIES.map((cat) => (
                                    <option key={cat.value} value={cat.value}>
                                        {cat.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {formData.category === "reminder" && (
                            <div className="form-group">
                                <label>Reminder Date</label>
                                <input
                                    type="date"
                                    value={formData.reminderDate}
                                    onChange={(e) =>
                                        setFormData({ ...formData, reminderDate: e.target.value })
                                    }
                                />
                            </div>
                        )}

                        <div style={{ display: "flex", gap: "0.5rem" }}>
                            <button type="submit" className="btn-primary">
                                {editingId ? "Update Note" : "Create Note"}
                            </button>
                            <button type="button" onClick={handleCancel} className="btn-secondary">
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* Search and Filter */}
            <div className="form-card" style={{ marginBottom: "1.5rem", padding: "1rem" }}>
                <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
                    <div className="form-group" style={{ margin: 0, flex: 1, minWidth: "200px" }}>
                        <input
                            type="text"
                            placeholder="Search notes..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <div className="form-group" style={{ margin: 0, flex: 1, minWidth: "200px" }}>
                        <select
                            value={filterCategory}
                            onChange={(e) => setFilterCategory(e.target.value)}
                        >
                            <option value="all">All Categories</option>
                            {CATEGORIES.map((cat) => (
                                <option key={cat.value} value={cat.value}>
                                    {cat.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            {/* Notes Grid */}
            <div className="dashboard-card">
                <div className="card-header">
                    <h3 className="card-title">All Notes</h3>
                </div>
                {loading ? (
                    <div className="card-loading">Loading notes...</div>
                ) : filteredNotes.length === 0 ? (
                    <div className="card-empty">No notes found. Create your first note!</div>
                ) : (
                    <div className="announcements-full-list">
                        {filteredNotes.map((note) => (
                            <div key={note.id} className="announcement-card" style={{ display: 'flex', flexDirection: 'column' }}>
                                {/* Card Header */}
                                <div className="announcement-card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                                    <div style={{ flex: 1 }}>
                                        <h3 style={{ margin: 0, fontWeight: 600, fontSize: "1rem", color: "#111827" }}>
                                            {note.title}
                                        </h3>
                                        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap", marginTop: "0.5rem" }}>
                                            <span className={`status-pill status-pill--${note.category === 'urgent' ? 'red' : note.category === 'reminder' ? 'blue' : note.category === 'todo' ? 'amber' : 'gray'}`} style={{ fontSize: "0.7rem", padding: "0.15rem 0.4rem" }}>
                                                {CATEGORIES.find((c) => c.value === note.category)?.label || note.category}
                                            </span>
                                            {note.reminderDate && (
                                                <span style={{ fontSize: "0.75rem", fontWeight: 600, color: "#b45309", background: "#fef3c7", padding: "0.15rem 0.5rem", borderRadius: "99px" }} title="Reminder Date">
                                                    ⏰ {new Date(note.reminderDate).toLocaleDateString()}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => togglePinNote(note.id, note.isPinned)}
                                        style={{ 
                                            background: "transparent", 
                                            border: "none", 
                                            cursor: "pointer", 
                                            fontSize: "1.2rem",
                                            opacity: note.isPinned ? 1 : 0.3
                                        }}
                                        title={note.isPinned ? "Unpin" : "Pin"}
                                    >
                                        📌
                                    </button>
                                </div>

                                {/* Card Content */}
                                <p className="announcement-card-msg" style={{ marginTop: "0.75rem", color: "#374151", lineHeight: 1.5 }}>
                                    {note.content.slice(0, 150)}{note.content.length > 150 ? "..." : ""}
                                </p>

                                {/* Card Footer */}
                                <div className="announcement-card-meta">
                                    <span style={{ fontSize: "0.75rem" }}>
                                        {note.createdAt?.toDate ? new Date(note.createdAt.toDate()).toLocaleDateString() : "Recently"}
                                    </span>
                                </div>

                                {/* Actions */}
                                <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.75rem", borderTop: "1px solid #e5e7eb", paddingTop: "0.75rem" }}>
                                    <button
                                        onClick={() => handleEdit(note)}
                                        className="btn-secondary"
                                        style={{ fontSize: "0.8rem", padding: "0.4rem 0.8rem" }}
                                    >
                                        ✏️ Edit
                                    </button>
                                    <button
                                        onClick={() => {
                                            if (window.confirm("Delete this note?")) {
                                                deleteNote(note.id).then(() => toast.success("Note deleted"));
                                            }
                                        }}
                                        className="btn-secondary"
                                        style={{ fontSize: "0.8rem", padding: "0.4rem 0.8rem", color: "#dc2626" }}
                                    >
                                        🗑️ Delete
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </Layout>
    );
}
